class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    this.closingTimeout;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

    this.openHandler = this.createOpenHandler();
    this.closeWithClosingClassHandler = this.closeWithClosingClass.bind(this);

    this.closeButton = this.querySelector('.disclosure__close-button');
    if(this.closeButton) {
      this.onClickCloseButtonHandler = this.onClickCloseButton.bind(this);
      this.closeButton.addEventListener('click', this.onClickCloseButtonHandler);
    }
    
    if(!this.dataset.ignoreHover) {
      this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;

      this.debouncedOnChange = debounce((event) => {
        this.isAbleToUseMouse = matchMedia('(hover: hover)').matches;
        this.initHoverDetails();
      }, 300);

      this.debouncedOnChangeHandler = this.debouncedOnChange.bind(this);
  
      window.addEventListener('resize', this.debouncedOnChangeHandler);
  
      this.initHoverDetails();
    }
    
    this.initClickSummary();
  }

  createOpenHandler() {
    return this.open.bind(this);
  }

  disconnectedCallback() {
    if(this.isAbleToUseMouse) {
      Shopify.removeEventListener(this.mainDetailsToggle, 'mouseenter', this.openHandler);
      Shopify.removeEventListener(this.mainDetailsToggle, 'mouseleave', this.closeWithClosingClassHandler);
    }

    if(this.closeButton) {
      this.closeButton.removeEventListener('click', this.onClickCloseButtonHandler);
    }

    if(this.debouncedOnChangeHandler) {
      window.removeEventListener('resize', this.debouncedOnChangeHandler);
    }
  }

  initHoverDetails () {
    if(this.isAbleToUseMouse) {
      Shopify.addListener(this.mainDetailsToggle, 'mouseenter', this.openHandler);
      Shopify.addListener(this.mainDetailsToggle, 'mouseleave', this.closeWithClosingClassHandler);
    } else {
      this.mainDetailsToggle.removeEventListener('mouseenter', this.openHandler);
      this.mainDetailsToggle.removeEventListener('mouseleave', this.closeWithClosingClassHandler);
    }
  }

  initClickSummary() {
    this.mainDetailsToggle.querySelector('summary').addEventListener('click', (e) => {
      e.preventDefault();
      if (this.mainDetailsToggle.hasAttribute('open')) {
        this.closeWithClosingClass();
      } else {
        this.open();
      }
    });
  }

  closeWithClosingClass() {
    this.isOpen = false;
    if(this.classList.contains('ignore-close')) return;
    this.mainDetailsToggle.classList.add('closing-start');
    if(this.closingTimeout) {
      clearTimeout(this.closingTimeout);
    }
    this.closingTimeout = setTimeout(() => {
      if (!this.isOpen) this.mainDetailsToggle.classList.add('closing');
      this.mainDetailsToggle.classList.remove('closing-start');
      setTimeout(() => {
        this.close();
        this.mainDetailsToggle.classList.remove('closing');
      }, 300);
    }, 300);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    this.closeWithClosingClass();
    this.mainDetailsToggle.querySelector('summary').focus();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeWithClosingClass();
    })
  }

  onClickCloseButton() {
    this.closeWithClosingClass();
  }

  playAnimation() {
    if (!this.animations) this.animations = this.content.getAnimations();
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach(animation => animation.play());
    } else {
      this.animations.forEach(animation => animation.cancel());
    }
  }

  open() {
    this.isOpen = true;
    this.mainDetailsToggle.setAttribute('open', true);
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', true);
    if (!this.mainDetailsToggle.classList.contains('closing-start')) {
      this.playAnimation();
    }
  }

  close() {
    if (this.isOpen) return;
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

let hoverSupport = matchMedia('(hover: hover)').matches;
window.addEventListener('resize', () => {
  hoverSupport = matchMedia('(hover: hover)').matches;
});

class HeaderMenu extends DetailsDisclosure {
  createOpenHandler() {
    const shouldDelay = !!(this.mainDetailsToggle && this.mainDetailsToggle.classList.contains('mega-menu'));
    this.shouldDelayHoverOpen = shouldDelay;
    if(!shouldDelay) {
      return super.createOpenHandler();
    }
    this.hoverOpenDelay = 0;
    this.hoverOpenTimeout = null;
    return this.openWithDelay.bind(this);
  }

  constructor() {
    super();
    this.header = document.querySelector('.header');
    this.outsideViewportCheckTimeout = null;
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
    this.querySelectorAll(':scope > details > .header__menu-item--link').forEach(element => element.addEventListener('click', this.onClickSummary.bind(this)));
    this.stickyHeader = this.closest('sticky-header');
  }

  disconnectedCallback() {
    if(this.shouldDelayHoverOpen) {
      this.clearHoverOpenTimeout();
    }
    this.clearOutsideViewportTimeout();
    super.disconnectedCallback();
  }

  onClickSummary(event) {
    if(hoverSupport && !this.dataset.ignoreHover) {
      event.preventDefault();
      window.location = event.currentTarget.dataset.href;
    }
  }

  clearHoverOpenTimeout() {
    if(this.hoverOpenTimeout) {
      clearTimeout(this.hoverOpenTimeout);
      this.hoverOpenTimeout = null;
    }
  }

  clearOutsideViewportTimeout() {
    if(this.outsideViewportCheckTimeout) {
      clearTimeout(this.outsideViewportCheckTimeout);
      this.outsideViewportCheckTimeout = null;
    }
  }

  openWithDelay() {
    if(!this.shouldDelayHoverOpen) {
      this.open();
      return;
    }
    if(this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.classList.contains('closing-start')) return;
    this.clearHoverOpenTimeout();
    this.hoverOpenTimeout = setTimeout(() => {
      this.clearHoverOpenTimeout();
      this.open();
    }, this.hoverOpenDelay);
  }

  closeWithClosingClass() {
    if(this.shouldDelayHoverOpen) {
      this.clearHoverOpenTimeout();
    }
    super.closeWithClosingClass();
  }

  open() {
    if(this.shouldDelayHoverOpen) {
      this.clearHoverOpenTimeout();
    }
    super.open();
    if(this.stickyHeader) {
      this.stickyHeader.setPreventOpen(true);
      this.stickyHeader.setPreventHide(true);
    }
  }

  close() {
    if(this.shouldDelayHoverOpen) {
      this.clearHoverOpenTimeout();
    }
    this.clearOutsideViewportTimeout();
    super.close();
    if(this.stickyHeader) {
      this.stickyHeader.setPreventOpen(false);
      this.stickyHeader.setPreventHide(false);
    }
  }

  onToggle() {
    if (!this.header) return;
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') === '') {
      document.documentElement.style.setProperty('--header-bottom-position-desktop', `${Math.floor(this.header.getBoundingClientRect().bottom)}px`);
    }
    if(this.mainDetailsToggle.hasAttribute('open')) {
      this.clearOutsideViewportTimeout();
      this.outsideViewportCheckTimeout = setTimeout(() => {
        this.outsideViewportCheckTimeout = null;
        if(!this.mainDetailsToggle.hasAttribute('open')) return;
        if(this.content.getBoundingClientRect().bottom > document.documentElement.clientHeight - 50) {
          this.content.classList.add('outside-viewport');
          document.body.classList.add('overflow-hidden');
          this.content.style.setProperty(
            "--top-position",
            `${this.content.getBoundingClientRect().top}px`
          );
        }
      }, 1000);
    } else {
      this.clearOutsideViewportTimeout();
      this.content.classList.remove('outside-viewport');
      document.body.classList.remove('overflow-hidden');
      this.content.style.removeProperty("--top-position");
    }
  }
}

customElements.define('header-menu', HeaderMenu);
